import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../reducers/reducers';
import * as React from 'react';
import {
    selectFeaturesAndCapabilitiesPerSubscriptionStatus,
    selectAppGroupIdForApp,
    selectAppName,
    selectAppPlatform,
    selectDisabledFeatureData,
    selectOrg,
    selectApp,
    selectAppStoreType,
    selectIsTrialEnded,
    selectIsTrialStarted,
    selectTrialType,
    selectCurrentSubscriptionStatus,
} from '../../selectors/selectors';
import { SubscriptionFeatureType } from '../../types';
import { AppComponentProps, OrgSettingsTabType } from '../../typescriptHelpers';
import { tooltipContentForFeatureOrCapabilityUsingTrialType } from '../planRestrictions/planRestrictionHelperComponents';
import Text from 'antd/lib/typography/Text';
import { DeleteAppRequest, tryDeleteApp, TRY_DELETE_APP_ENTITY } from '../../actions/app';
import { useHistory } from 'react-router-dom';
import Popconfirm from 'antd/lib/popconfirm';
import PermissionedActionButton from '../HOCs/PermissionedActionButton';
import { RunwayUserAction } from '../../helpers/aclHelpers';
import { runwayColors } from '../styles/globalStyles';
import { globalMessage } from '../../utils/globalMessage';
import { createOrgSettingsRoute } from '../../helpers/routeHelpers';
import { DeleteOutlined } from '@ant-design/icons';
import { displayTextForAppPlatform } from '../../helpers/displayHelpers';
import Space from 'antd/lib/space';
import SubscriptionPlanIcon from '../planRestrictions/SubscriptionPlanIcon';
import { useFirstIncompleteQuickStartCIStep } from '../ciQuickstart/hooks/useQuickStartCISteps';
import { QuickStartCIProgressBanner } from '../ciQuickstart/QuickstartCIProgressBanner';
import { useError, useIsLoading, useWillUpdate } from '@/utils/hooks/helpers';

const RestrictedAppComponent: React.FC<AppComponentProps> = (props: AppComponentProps) => {
    const { orgId, appId } = props;
    const dispatch = useDispatch();
    const history = useHistory();

    const isTrialStarted = useSelector((state: RootState) => selectIsTrialStarted(state));
    const isTrialEnded = useSelector((state: RootState) => selectIsTrialEnded(state));
    const trialType = useSelector((state: RootState) => selectTrialType(state));
    const subscriptionStatus = useSelector((state: RootState) => selectCurrentSubscriptionStatus(state));

    const featureData = useSelector((state: RootState) =>
        selectDisabledFeatureData(state, props.orgId, SubscriptionFeatureType.AddApps),
    );
    const appGroupId = useSelector((state: RootState) => selectAppGroupIdForApp(state, props.appId));
    const appName = useSelector((state: RootState) => selectAppName(state, props.appId));
    const appPlatform = useSelector((state: RootState) => selectAppPlatform(state, props.appId));
    const appStoreType = useSelector((state: RootState) => selectAppStoreType(state, props.appId));
    const orgIdParam = useSelector((state: RootState) => selectOrg(state, props.orgId).orgId);
    const appIdParam = useSelector((state: RootState) => selectApp(state, props.appId).appId);

    const featuresAndCapabilitiesPerSubscriptionStatus = useSelector((state: RootState) =>
        selectFeaturesAndCapabilitiesPerSubscriptionStatus(state),
    );

    const deleteAppIsLoading = useIsLoading(TRY_DELETE_APP_ENTITY, props.appId);
    const deleteAppError = useError(TRY_DELETE_APP_ENTITY, props.appId);
    const willDeleteApp = useWillUpdate(TRY_DELETE_APP_ENTITY, props.appId);

    const [firstIncompleteQuickStartCIStepIndex, quickStartCIStepCount] = useFirstIncompleteQuickStartCIStep(
        appId,
        orgId,
    );
    const quickStartCIProgress = ((firstIncompleteQuickStartCIStepIndex + 1) / quickStartCIStepCount) * 100;
    const showQuickStartCI = !!(quickStartCIProgress < 100 && firstIncompleteQuickStartCIStepIndex);

    const deleteApp = () => {
        const request: DeleteAppRequest = {
            orgId: props.orgId,
            appId: props.appId,
            appGroupId,
            orgIdParam,
            appIdParam,
        };

        dispatch(tryDeleteApp(request));
    };

    React.useEffect(() => {
        if (deleteAppError) {
            globalMessage.error(deleteAppError);
        }
    }, [deleteAppError]);

    React.useEffect(() => {
        if (willDeleteApp) {
            globalMessage.success('App deleted!');

            const orgRoute = createOrgSettingsRoute(orgIdParam, OrgSettingsTabType.Team);

            history.replace(orgRoute);
        }
    }, [willDeleteApp]);

    const restrictionDescription = tooltipContentForFeatureOrCapabilityUsingTrialType({
        orgId: orgId,
        feature: SubscriptionFeatureType.AddApps,
        featureCapabilityOrAutomationDisabledData: featureData,
        isTrialStarted: isTrialStarted,
        isTrialEnded: isTrialEnded,
        trialType: trialType,
        subscriptionStatus,
        featuresAndCapabilitiesPerSubscriptionStatus,
    });
    const platformDisplayText = displayTextForAppPlatform(appPlatform, appStoreType);

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: showQuickStartCI ? 'flex-start' : 'center',
                alignItems: 'stretch',
                height: '100%',
                padding: 34,
            }}
        >
            {showQuickStartCI && <QuickStartCIProgressBanner appId={appId} orgId={orgId} />}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20 }}>
                <Space align="center">
                    <SubscriptionPlanIcon />
                    <Text style={{ fontSize: 18 }}>
                        {appName} ({platformDisplayText}) is restricted.
                    </Text>
                </Space>
                {restrictionDescription}
                <Text type="secondary">Or</Text>
                <Popconfirm
                    title={`Are you sure you want to delete app: ${appName} (${platformDisplayText})?`}
                    onConfirm={deleteApp}
                    okButtonProps={{ danger: true }}
                    okText="Yes, delete"
                    placement="topRight"
                >
                    <PermissionedActionButton
                        actionType={RunwayUserAction.UAAddRemoveEditIntegration}
                        danger
                        loading={deleteAppIsLoading}
                        icon={<DeleteOutlined style={{ color: runwayColors.statusRed }} />}
                    >
                        Delete app
                    </PermissionedActionButton>
                </Popconfirm>
            </div>
        </div>
    );
};

export default RestrictedAppComponent;
